import React from 'react';
import LegalLayout from './LegalLayout';

const Privacy = () => (
  <LegalLayout title="Privacy Policy" lastUpdated="2026-10-14">
    <p>
      Gainrhino ("we", "our", "us") operates the Gainrhino CRM platform at{' '}
      <a href="https://crm.gainrhino.com" className="text-primary-600 hover:underline">crm.gainrhino.com</a>{' '}
      (the "Service"). This Privacy Policy explains what data we collect, how
      we use it, and the choices you have.
    </p>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">1. Who this policy is for</h2>
    <p>
      Gainrhino is a B2B customer-relationship management tool used by small businesses
      (primarily dental and service-based clinics) to manage their inbound leads. This
      policy describes how we handle two groups of people:
    </p>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>Customers</strong> — the businesses and their employees who sign up for and use Gainrhino.</li>
      <li><strong>End users</strong> — individuals whose contact details are captured by our customers via channels such as Facebook Lead Ads, web forms, SMS, email, or manual entry.</li>
    </ul>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">2. What we collect</h2>
    <p>For <strong>Customers</strong>:</p>
    <ul className="list-disc pl-6 space-y-1">
      <li>Account information (name, email, hashed password, role, organization).</li>
      <li>Integration credentials necessary to deliver the Service (e.g. SMTP settings, VoIP provider credentials, Facebook Page Access Tokens, Meta Conversions API tokens) — encrypted at rest.</li>
      <li>Usage telemetry such as logs of API requests, automation runs and webhook delivery activity.</li>
    </ul>
    <p>For <strong>End users</strong>:</p>
    <ul className="list-disc pl-6 space-y-1">
      <li>Contact details our customers' forms ask for (name, email, phone) and any other fields the customer has chosen to collect.</li>
      <li>Communications between the customer and the end user (SMS, email, notes, call logs) recorded by the customer's team within the Service.</li>
      <li>Lead source metadata (e.g. which Facebook ad or web form a submission came through).</li>
    </ul>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">3. How we use it</h2>
    <p>We use this data only to operate the Service:</p>
    <ul className="list-disc pl-6 space-y-1">
      <li>Routing inbound messages to the correct customer's CRM inbox.</li>
      <li>Allowing customer staff to follow up via SMS, email and other channels we integrate.</li>
      <li>Running customer-defined automations (e.g. "send welcome SMS when a new lead arrives").</li>
      <li>Generating analytics and reports for the customer.</li>
      <li>When the customer turns on the Meta Conversions API integration: reporting the pipeline stage of leads that came from the customer's own Facebook or Instagram lead ads (for example "Appointment Booked") to the customer's own Meta dataset. Only the stage name the customer chose, the time, Meta's lead ID and SHA-256 hashed email, phone and record ID are sent. The customer picks which stages are sent and can disconnect at any time.</li>
      <li>Maintaining audit logs and securing the Service.</li>
    </ul>

    <h3 className="text-lg font-semibold text-gray-900 pt-2">AI assistants a Customer connects (Claude and other MCP clients)</h3>
    <p>
      A Customer's user can connect an AI assistant such as Anthropic's Claude to their Gainrhino
      account through our MCP connector (see{' '}
      <a href="/integrations/claude" className="text-primary-600 hover:underline">Claude integration</a>).
      The user approves the connection on a Gainrhino consent screen, and the assistant then acts as
      that user, with that user's role permissions, inside that user's organization only.
    </p>
    <ul className="list-disc pl-6 space-y-1">
      <li>We send data to the assistant only in response to the tool requests it makes on the user's behalf, for example lead details, timeline notes, tasks or report figures. That data may include End user personal information and, for healthcare customers, health information that staff recorded in the CRM.</li>
      <li>Once delivered, the data is processed by the assistant's provider under the Customer's own agreement with that provider (for Claude, Anthropic's terms and privacy policy). Customers that handle protected health information are responsible for using an assistant plan covered by an appropriate agreement, such as a Business Associate Agreement.</li>
      <li>Actions the assistant takes (for example adding a note or sending an email) are performed as the connecting user and recorded in the audit trail.</li>
      <li>Access tokens are stored only as hashes, expire after one hour, and are revoked when the user disconnects the app under Settings → Connect Claude, changes their password, or is deactivated.</li>
    </ul>
    <p>We do <strong>not</strong> sell personal data, and we do not use End user data to train machine-learning models or for cross-customer analytics.</p>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">4. Sub-processors</h2>
    <p>The Service depends on a small number of trusted third-party providers:</p>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>Meta Platforms, Inc.</strong> — Lead Ads webhook delivery, Graph API, and (when the customer enables it) the Conversions API.</li>
      <li><strong>NetSapiens / VoIP.ms</strong> — call detail records, recording playback, and SMS delivery (depending on the VoIP provider the customer has selected).</li>
      <li><strong>OpenAI / Mistral AI</strong> — optional AI features for drafting messages and OCR-scanning business cards. Only the specific text or image the customer submits to those features is sent.</li>
      <li><strong>OpenRouter</strong> (routing to Anthropic and OpenAI models) — the optional in-app AI assistant. Only the conversation and the CRM records the assistant looks up for that conversation are sent.</li>
      <li><strong>Cloudflare</strong> — DNS, TLS termination, basic DDoS protection.</li>
      <li>Our infrastructure provider for hosting and database storage.</li>
    </ul>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">5. Data retention</h2>
    <p>
      End user data is retained for as long as the Customer keeps their Gainrhino
      account active. When a Customer cancels, their data is deleted within 30 days
      unless legal or accounting requirements dictate otherwise. Backups are
      automatically purged within 60 days.
    </p>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">6. Your rights</h2>
    <p>
      End users have the right to request access, correction or deletion of their
      personal data. See <a href="/data-deletion" className="text-primary-600 hover:underline">Data Deletion Request</a> for the procedure.
      Customers can perform these operations directly from within their Gainrhino dashboard.
    </p>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">7. Security</h2>
    <p>
      We use HTTPS for all network traffic, encrypt sensitive credentials at rest,
      run regular automated backups, and apply role-based access control across the
      platform. No system is perfectly secure, but we treat our customers' and end
      users' data as if it were our own.
    </p>

    <h2 className="text-xl font-semibold text-gray-900 pt-4">8. Contact</h2>
    <p>
      Questions, concerns or requests? Email{' '}
      <a href="mailto:info@gainrhino.com" className="text-primary-600 hover:underline">info@gainrhino.com</a>{' '}
      and we will reply within 5 business days.
    </p>
  </LegalLayout>
);

export default Privacy;
